// ═══════════════════════════════════════════════════════════════════════════
// OPTIMIZED EMPLOYEE EDIT DIALOG - NO TYPING DELAY
// ═══════════════════════════════════════════════════════════════════════════
// Separate component prevents parent table from re-rendering on every keystroke
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, memo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import { CreateDialog, FieldLabel } from "./shared";

const EMPLOYEE_DESIGNATION_OPTIONS = [
  { value: "Operator", label: "Operator" },
  { value: "Asistant Operator", label: "Asistant Operator" },
  { value: "Helper", label: "Helper" },
  { value: "machinesupervisor", label: "Machine Supervisor" },
  { value: "Supervisor", label: "Supervisor" },
  { value: "HR", label: "HR" },
  { value: "Planner", label: "Planner" },
  { value: "Admin", label: "Admin" },
];

// ✅ Separate component - parent won't re-render on typing
const EmployeeEditDialog = memo(function EmployeeEditDialog({
  open,
  onClose,
  onSave,
  saving,
  employee,  // Initial employee data
}) {
  // ✅ Local state - only this dialog re-renders on typing
  const [form, setForm] = useState({
    EmpID: "",
    Name: "",
    MachineGroup: "",
    Designation: "Operator",
    ActiveStatus: 1,
  });

  // Update form when employee changes
  useEffect(() => {
    if (employee) {
      setForm({
        EmpID: employee.EmpID || "",
        Name: employee.Name || "",
        MachineGroup: employee.MachineGroup || "",
        Designation: employee.Designation || "Operator",
        ActiveStatus: employee.ActiveStatus ?? 1,
      });
    }
  }, [employee]);

  const handleClose = () => {
    onClose();
  };

  const handleSave = () => {
    onSave(form);
  };

  return (
    <CreateDialog
      open={open}
      onClose={handleClose}
      title="Edit Employee"
      icon={<EditIcon />}
      color="#059669"
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <FieldLabel>Employee ID</FieldLabel>
          <TextField
            fullWidth
            size="small"
            value={form.EmpID}
            disabled  // Can't change Employee ID
          />
        </Grid>

        <Grid item xs={12} sm={8}>
          <FieldLabel>Name *</FieldLabel>
          <TextField
            fullWidth
            size="small"
            value={form.Name}
            onChange={(e) => setForm((f) => ({ ...f, Name: e.target.value }))}
            autoFocus
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FieldLabel>Machine Group *</FieldLabel>
          <TextField
            fullWidth
            size="small"
            value={form.MachineGroup}
            onChange={(e) =>
              setForm((f) => ({ ...f, MachineGroup: e.target.value }))
            }
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FieldLabel>Designation</FieldLabel>
          <FormControl fullWidth size="small">
            <Select
              value={form.Designation}
              onChange={(e) =>
                setForm((f) => ({ ...f, Designation: e.target.value }))
              }
            >
              {EMPLOYEE_DESIGNATION_OPTIONS.map((d) => (
                <MenuItem key={d.value} value={d.value}>
                  {d.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FieldLabel>Active Status</FieldLabel>
          <FormControl fullWidth size="small">
            <Select
              value={form.ActiveStatus}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  ActiveStatus: Number(e.target.value),
                }))
              }
            >
              <MenuItem value={1}>Active</MenuItem>
              <MenuItem value={0}>Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
        <Button
          onClick={handleClose}
          color="inherit"
          sx={{ textTransform: "none" }}
          disabled={saving}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={
            saving ? (
              <CircularProgress size={15} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </Box>
    </CreateDialog>
  );
});

export default EmployeeEditDialog;
